import React, { useMemo, useState } from "react";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CCard,
  CCardBody,
  CRow,
  CCol,
  CFormLabel,
  CFormSelect,
  CButton,
  CSpinner,
  CFormCheck,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CCardHeader,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilFilter, cilCloudDownload } from "@coreui/icons";
import DatePicker from "react-datepicker";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBar } from "@nivo/bar";
import SafeTooltip from "../../../components/SafeTooltip/SafeTooltip";
import ZoomableChartWrapper from "../../../components/ZoomableChartWrapper/ZoomableChartWrapper";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import i18n from "../../../../i18n";

// --- Вспомогательные функции ---
const formatDuration = (seconds) => {
  if (seconds < 0 || !seconds) seconds = 0;
  const h = Math.floor(seconds / 3600).toString().padStart(2, "0");
  const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
};

const getMetricValue = (dataObj, key) => {
  if (!dataObj) return 0;
  const keys = key.split(".");
  let val = dataObj;
  for (const k of keys) {
    if (val === undefined || val === null) return 0;
    val = val[k];
  }
  return val || 0;
};

const getMetricValueLocal = (dataObj, key) => getMetricValue(dataObj, key);

const formatMetric = (value, type) => {
  if (type.includes("time")) return formatDuration(value);
  if (type.includes("mass_tons")) return `${(value / 1000).toFixed(1)} т`;
  if (type.includes("float")) return value.toFixed(1);
  if (type.includes("sec")) return `${value.toFixed(1)} сек`;
  if (type.includes("pct")) return `${value.toFixed(1)}%`;
  return value;
};

// Функция для раскраски KPI процентов
const getUiKpiStyle = (val) => {
  if (typeof val !== "number") return {};
  if (val <= 40) return { backgroundColor: "rgba(255, 107, 107, 0.25)", color: "#b30000", fontWeight: "bold" };
  if (val <= 60) return { backgroundColor: "rgba(255, 224, 102, 0.35)", color: "#8a6a00", fontWeight: "bold" };
  return { backgroundColor: "rgba(81, 207, 102, 0.25)", color: "#006600", fontWeight: "bold" };
};

// Функция для Heatmap (цветового градиента) от худшего к лучшему
const getUiHeatmapStyle = (val, min, max, reverse = false) => {
  if (typeof val !== "number" || val == null || isNaN(val) || min === max) return { fontWeight: "600" };
  let ratio = (val - min) / (max - min);
  if (reverse) ratio = 1 - ratio; // Инверсия для простоев

  let r, g, b;
  if (ratio < 0.5) {
    let norm = ratio * 2;
    r = 255; g = Math.round(128 + (127 * norm)); b = 128;
  } else {
    let norm = (ratio - 0.5) * 2;
    r = Math.round(255 - (102 * norm)); g = 255; b = Math.round(128 + (51 * norm));
  }
  return { backgroundColor: `rgba(${r}, ${g}, ${b}, 0.35)`, fontWeight: "600" };
};


const SummaryAnalyticsModal = ({
  visible,
  onClose,
  t,
  isSuperuser,
  currentLang,
  multiPreset,
  handleMultiPresetChange,
  multiStartDate,
  setMultiStartDate,
  multiEndDate,
  setMultiEndDate,
  multiLoading,
  fetchMultiAnalytics,
  multiData,
  showMetricsFilter,
  setShowMetricsFilter,
  selectedMetrics,
  setSelectedMetrics,
  COMPARISON_METRICS,
  classIdToMeta,
  // Эти пропсы мы принимаем для совместимости, но игнорируем,
  // так как логика экспорта теперь полностью инкапсулирована внутри модалки.
  exportingMultiExcel,
  handleExportMultiExcel,
  multiCooldown
}) => {
  // Локальное состояние для загрузки Excel
  const [localExporting, setLocalExporting] = useState(false);

  // --- Агрегации ---
  const fleetAggregate = useMemo(() => {
    if (multiData.length === 0) return null;
    let sums = { tWork: 0, tDowntime: 0, tReady: 0, tCalendar: 0, vFact: 0, vNorm: 0 };
    multiData.forEach((res) => {
      if (res.data && res.data.kpis) {
        sums.tWork += res.data.kpis.tWork || 0;
        sums.tDowntime += res.data.kpis.tDowntime || 0;
        sums.tReady += res.data.kpis.tReady || 0;
        sums.tCalendar += res.data.kpis.tCalendar || 0;
        sums.vFact += res.data.kpis.vFact || 0;
        sums.vNorm += res.data.kpis.vNorm || 0;
      }
    });

    const KIO = sums.tCalendar > 0 ? (sums.tWork / sums.tCalendar) * 100 : 0;
    const KTG = sums.tCalendar > 0 ? (sums.tReady / sums.tCalendar) * 100 : 0;
    const KIV = sums.tReady > 0 ? (sums.tWork / sums.tReady) * 100 : 0;
    const KIN = sums.vNorm > 0 ? (sums.vFact / sums.vNorm) * 100 : 0;
    const OEE = (KTG / 100) * (KIV / 100) * (KIN / 100) * 100;
    return { kpis: { KIO, KTG, KIV, KIN, OEE } };
  }, [multiData]);

  const fleetTruckStats = useMemo(() => {
    const stats = {};
    multiData.forEach((res) => {
      if (res.device.hide_truck_stats) {
        return;
      }
      if (res.data && res.data.truck_stats_summary) {
        Object.entries(res.data.truck_stats_summary).forEach(([truckName, tStat]) => {
          if (!stats[truckName]) {
            stats[truckName] = { arrivals: 0, cycles: 0, vol: 0, mass: 0, min_cycles: Infinity, max_cycles: 0 };
          }
          stats[truckName].arrivals += tStat.arrivals || 0;
          stats[truckName].cycles += tStat.cycles || 0;
          stats[truckName].vol += Number(tStat.vol || 0);
          stats[truckName].mass += Number(tStat.mass || 0);
          if (tStat.min_cycles !== undefined) stats[truckName].min_cycles = Math.min(stats[truckName].min_cycles, tStat.min_cycles);
          if (tStat.max_cycles !== undefined) stats[truckName].max_cycles = Math.max(stats[truckName].max_cycles, tStat.max_cycles);
        });
      }
    });
    Object.values(stats).forEach((s) => {
      if (s.min_cycles === Infinity) s.min_cycles = 0;
      s.avg_cycles = s.arrivals > 0 ? (s.cycles / s.arrivals).toFixed(1) : 0;
    });
    return stats;
  }, [multiData]);

  const getFleetMetricValue = (key, type) => {
    if (key.startsWith("kpis.")) {
      const kpiKey = key.split(".")[1];
      return fleetAggregate?.kpis[kpiKey] || 0;
    }
    if (type === "useful_time") {
      let tWorkSum = 0;
      let tUptimeSum = 0;
      multiData.forEach((res) => {
        if (res.data && !res.error && res.data.kpis) {
          tWorkSum += res.data.kpis.tWork || 0;
          const sched = res.data.total_duration_sec || 0;
          const noData = res.data.durations_by_class_sec?.no_data || 0;
          tUptimeSum += Math.max(0, sched - noData);
        }
      });
      return { tWork: tWorkSum, tUptime: tUptimeSum };
    }

    let sum = 0;
    let count = 0;
    multiData.forEach((res) => {
      if (res.data && !res.error) {
        const val = getMetricValue(res.data, key);
        if (val > 0 || type.includes("sum")) {
          sum += val;
          count += 1;
        }
      }
    });

    if (type.includes("avg")) return count > 0 ? sum / count : 0;
    return sum;
  };

  // --- Средние для графиков Nivo ---
  const avgVolume = useMemo(() => {
    const vols = multiData.map(r => Number(r.data?.estimated_volume_total || 0));
    return vols.length ? (vols.reduce((a, b) => a + b, 0) / vols.length).toFixed(1) : 0;
  }, [multiData]);

  const avgOEE = useMemo(() => {
    const oees = multiData.map(r => Number(r.data?.kpis?.OEE || 0));
    return oees.length ? (oees.reduce((a, b) => a + b, 0) / oees.length).toFixed(1) : 0;
  }, [multiData]);

  const volumeChartData = useMemo(() => {
    return multiData.filter((r) => !r.error && r.data).map((r) => ({
      device: r.device.name.length > 20 ? r.device.name.substring(0, 20) + "..." : r.device.name,
      volume: Number(r.data.estimated_volume_total || 0).toFixed(1),
      full_name: r.device.name,
    }));
  }, [multiData]);

  const oeeChartData = useMemo(() => {
    return multiData.filter((r) => !r.error && r.data).map((r) => ({
      device: r.device.name.length > 20 ? r.device.name.substring(0, 20) + "..." : r.device.name,
      oee: Number(r.data.kpis?.OEE || 0).toFixed(1),
      full_name: r.device.name,
    }));
  }, [multiData]);

  // --- Компонент Стрелочек (Трендов) ---
  const TrendArrow = ({ val, avg, reverseHeatmap }) => {
    if (val == null || isNaN(val) || avg == null || isNaN(avg)) return null;
    if (Math.abs(val - avg) / (avg || 1) < 0.01) return <span className="ms-2 text-muted">-</span>;

    const isGreater = val > avg;
    const isGood = reverseHeatmap ? !isGreater : isGreater;

    let className = "trend-arrow ";
    if (isGreater && isGood) className += "trend-up-good";
    if (!isGreater && !isGood) className += "trend-down-bad";
    if (isGreater && !isGood) className += "trend-up-bad";
    if (!isGreater && isGood) className += "trend-down-good";

    return (
      <span className={className} title={`В среднем по парку: ${typeof avg === 'number' ? avg.toFixed(1) : avg}`}>
        {isGreater ? "↑" : "↓"}
      </span>
    );
  };

  // --- Генерация Инсайтов ---
  const generateInsight = (label, rawValues, minV, maxV, reverseHeatmap) => {
    if (!rawValues || rawValues.length < 2) return <span className="text-muted" style={{ fontSize: '0.75rem' }}>Недостаточно данных</span>;

    const validData = rawValues.filter(d => d.num != null && !isNaN(d.num));
    if (validData.length === 0) return null;

    const maxDevice = validData.find(d => d.num === maxV)?.deviceName || "Неизвестно";
    const minDevice = validData.find(d => d.num === minV)?.deviceName || "Неизвестно";

    const diff = maxV - minV;
    const threshold = maxV * 0.1; // 10% разница

    if (reverseHeatmap) {
      if (maxV > 0 && diff > threshold) {
        return <div style={{ fontSize: '0.75rem' }}><strong className="text-danger">Проблема:</strong> {maxDevice} ({maxV.toFixed(1)})</div>;
      }
    } else {
      if (maxV > 0 && diff > threshold) {
        return <div style={{ fontSize: '0.75rem' }}><strong className="text-success">Лидер:</strong> {maxDevice} ({maxV.toFixed(1)})</div>;
      }
    }
    return <span className="text-muted" style={{ fontSize: '0.75rem' }}>Показатели ровные</span>;
  };

  // --- ИСПРАВЛЕННЫЙ ЭКСПОРТ EXCEL (Независимая функция) ---
  const handleExportToExcel = async () => {
    if (multiData.length === 0) {
      toast.warn("Сначала сформируйте отчет");
      return;
    }
    setLocalExporting(true);

    const getKpiFill = (val) => {
      if (typeof val !== 'number') return null;
      if (val <= 40) return { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF66" } };
      if (val <= 60) return { type: "pattern", pattern: "solid", fgColor: { argb: "FF99FF99" } };
      return { type: "pattern", pattern: "solid", fgColor: { argb: "FFFF8080" } };
    };

    const getHeatmapFill = (val, min, max, reverse = false) => {
      if (typeof val !== 'number' || max === min || val == null || isNaN(val)) return null;
      let ratio = (val - min) / (max - min);
      if (reverse) ratio = 1 - ratio;

      let r, g, b;
      if (ratio < 0.5) {
        let norm = ratio * 2;
        r = 255; g = Math.round(128 + (127 * norm)); b = 128;
      } else {
        let norm = (ratio - 0.5) * 2;
        r = Math.round(255 - (102 * norm)); g = 255; b = Math.round(128 + (51 * norm));
      }
      const toHex = (c) => c.toString(16).padStart(2, '0').toUpperCase();
      return { type: "pattern", pattern: "solid", fgColor: { argb: `FF${toHex(r)}${toHex(g)}${toHex(b)}` } };
    };

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "System AI";
      const sheet = workbook.addWorksheet("Сводная аналитика", { views: [{ showGridLines: false }] });

      const cols = [
        { key: "param", width: 45 },
        { key: "fleet", width: 25 },
      ];
      multiData.forEach((res, i) => {
        cols.push({ key: `dev_${i}`, width: 25 });
      });
      sheet.columns = cols;

      const titleRow = sheet.addRow({ param: "=== ИНФОРМАЦИЯ ОБ ОТЧЕТЕ ===" });
      titleRow.font = { bold: true, size: 12 };
      sheet.addRow({
        param: "Период:",
        fleet: `${dayjs(multiStartDate).format("DD.MM.YYYY HH:mm")} - ${dayjs(multiEndDate).format("DD.MM.YYYY HH:mm")}`
      });

      const uniqueZones = [...new Set(multiData.map(r => r.device.work_zone_name).filter(Boolean))];
      sheet.addRow({ param: "Участок:", fleet: uniqueZones.length > 0 ? uniqueZones.join(", ") : "Все / Без привязки" });

      const deviceNames = multiData.map(r => r.device.name).join(", ");
      sheet.addRow({ param: "Техника:", fleet: deviceNames });

      sheet.addRow({});

      const headerDict = {
        param: t("dashboard.parameter", "Параметр"),
        fleet: "В СРЕДНЕМ ПО ПАРКУ"
      };
      multiData.forEach((res, i) => {
        headerDict[`dev_${i}`] = res.device.name;
      });

      const headerRow = sheet.addRow(headerDict);
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
      headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
      headerRow.alignment = { vertical: "middle", horizontal: "center" };
      headerRow.height = 30;

      const activeMetrics = COMPARISON_METRICS.filter((m) => selectedMetrics.includes(m.key));
      const rowsData = [];

      // --- БЛОК 1: ОСНОВНЫЕ МЕТРИКИ ---
      activeMetrics.forEach((metric) => {
        if (metric.isTwin && !multiData.some((res) => res.data?.use_digital_twin)) return;

        const rowData = { param: metric.label, isKpi: metric.type === "pct", isHeatmap: metric.type !== "pct", rawValues: [] };
        const isReverse = metric.key.includes("downtime") || metric.key.includes("no_data") || metric.type.includes("avg_time") || metric.type.includes("avg_sec");
        rowData.reverse = isReverse;

        let fleetDisplay = "";
        let fleetNum = null;

        if (metric.type === "useful_time") {
          const f = getFleetMetricValue(metric.key, metric.type);
          fleetNum = f.tUptime > 0 ? (f.tWork / f.tUptime) * 100 : 0;

          const devWork = [], devUptime = [], devPct = [];
          multiData.forEach((res) => {
            if (res.error || !res.data) {
              devWork.push({ text: "Ошибка / {i18n.t('dashboard.no_data', 'Нет данных')}", num: null });
              devUptime.push({ text: "Ошибка / Нет данных", num: null });
              devPct.push({ text: "Ошибка / Нет данных", num: null });
            } else {
              const duMh = (res.data.kpis?.tWork || 0) / 3600.0;
              const sched = res.data.total_duration_sec || 0;
              const noData = res.data.durations_by_class_sec?.no_data || 0;
              const dtMh = Math.max(0, sched - noData) / 3600.0;
              const dpct = dtMh > 0 ? (duMh / dtMh) * 100 : 0;
              devWork.push({ text: `${duMh.toFixed(1)} маш.-ч`, num: duMh });
              devUptime.push({ text: `${dtMh.toFixed(1)} маш.-ч`, num: dtMh });
              devPct.push({ text: `${dpct.toFixed(1)}%`, num: dpct });
            }
          });

          rowsData.push({ param: "Полезное время (факт)", fleet: `${(f.tWork / 3600).toFixed(1)} маш.-ч`, rawValues: devWork, isHeatmap: true, reverse: false });
          rowsData.push({ param: "Полезное время (всего)", fleet: `${(f.tUptime / 3600).toFixed(1)} маш.-ч`, rawValues: devUptime, isHeatmap: true, reverse: false });
          rowsData.push({ param: "Полезное время (%)", fleet: `${fleetNum.toFixed(1)}%`, rawValues: devPct, isKpi: true });

        } else {
          // ИСПРАВЛЕНИЕ: Используем агрегированные данные по парку
          fleetNum = getFleetMetricValue(metric.key, metric.type);
          fleetDisplay = metric.key === "durations_by_class_sec.actual_downtime"
            ? formatMetric(fleetNum + getFleetMetricValue("durations_by_class_sec.no_data", "sum_time"), metric.type)
            : formatMetric(fleetNum, metric.type);

          multiData.forEach((res) => {
            if (res.error || !res.data) {
              rowData.rawValues.push({ text: "Ошибка / Нет данных", num: null });
            } else {
              let val = getMetricValueLocal(res.data, metric.key);
              if (metric.key === "durations_by_class_sec.actual_downtime") {
                val += getMetricValueLocal(res.data, "durations_by_class_sec.no_data");
              }
              rowData.rawValues.push({ text: formatMetric(val, metric.type), num: val });
            }
          });
          rowData.fleet = fleetDisplay;
          rowsData.push(rowData);
        }
      });

      // --- БЛОК 2: ДЕТАЛИЗАЦИЯ ПО СМЕНАМ ---
      const allShiftNames = [...new Set(multiData.filter((r) => !r.error && r.data?.shift_stats).flatMap((r) => Object.keys(r.data.shift_stats)))];
      if (allShiftNames.length > 0) {
        rowsData.push({ isSpacer: true });
        rowsData.push({ param: "=== ДЕТАЛИЗАЦИЯ ПО СМЕНАМ ===", isHeader: true });

        allShiftNames.forEach((shiftName) => {
          rowsData.push({ param: `Смена: ${shiftName}`, isSubHeader: true });

          const params = [
            { key: "cycles_tot", label: "  Всего циклов", type: "num" },
            { key: "cycles_veh", label: "  ├─ В самосвал", type: "num" },
            { key: "cycles_gnd", label: "  └─ В отвал", type: "num" },
            { key: "vol_tot", label: "  Объем (всего) м³", type: "float" },
            { key: "vol_veh", label: "  ├─ В самосвал м³", type: "float" },
            { key: "vol_gnd", label: "  └─ В отвал м³", type: "float" },
            { key: "mass_tot", label: "  Масса (всего) т", type: "mass" },
            { key: "mass_veh", label: "  ├─ В самосвал т", type: "mass" },
            { key: "mass_gnd", label: "  └─ В отвал т", type: "mass" },
            { key: "dur_dig", label: "  Копание / Поворот", type: "time" },
            { key: "dur_unload_veh", label: "  ├─ Разгрузка в авто", type: "time" },
            { key: "dur_unload_gnd", label: "  └─ Разгрузка в отвал", type: "time" },
            { key: "downtime", label: "  Простой / Нет связи", type: "time", reverse: true },
          ];

          params.forEach((p) => {
            const rData = { param: p.label, fleet: "-", rawValues: [], isHeatmap: true, reverse: p.reverse };
            multiData.forEach((res) => {
              if (res.error || !res.data?.shift_stats?.[shiftName]) {
                rData.rawValues.push({ text: "-", num: null });
              } else {
                const s = res.data.shift_stats[shiftName];
                let val = 0;
                if (p.key === "cycles_tot") val = s.cycles_total || 0;
                if (p.key === "cycles_veh") val = s.cycles_vehicle || 0;
                if (p.key === "cycles_gnd") val = s.cycles_ground || 0;
                if (p.key === "vol_tot") val = s.volume_total || 0;
                if (p.key === "vol_veh") val = s.volume_vehicle || 0;
                if (p.key === "vol_gnd") val = s.volume_ground || 0;
                if (p.key === "mass_tot") val = s.mass_total || 0;
                if (p.key === "mass_veh") val = s.mass_vehicle || 0;
                if (p.key === "mass_gnd") val = s.mass_ground || 0;
                if (p.key === "dur_dig") val = (s.durations?.[3] || 0) + (s.durations?.[0] || 0);
                if (p.key === "dur_unload_veh") val = s.durations?.[1] || 0;
                if (p.key === "dur_unload_gnd") val = s.durations?.[2] || 0;
                if (p.key === "downtime") val = (s.actual_downtime || 0) + (s.no_data || 0);

                let text = val;
                if (p.type === "float") text = Number(val).toFixed(1);
                if (p.type === "mass") { val = val / 1000; text = val.toFixed(1); }
                if (p.type === "time") text = formatDuration(val);
                rData.rawValues.push({ text: text.toString(), num: val });
              }
            });
            rowsData.push(rData);
          });
        });
      }

      // --- БЛОК 3: СТАТИСТИКА ПО САМОСВАЛАМ ---
      const allTruckNames = Object.keys(fleetTruckStats);
      if (allTruckNames.length > 0) {
        rowsData.push({ isSpacer: true });
        rowsData.push({ param: "=== СТАТИСТИКА ПО САМОСВАЛАМ ===", fleet: "СУММА ПО ПАРКУ", isHeader: true });

        allTruckNames.forEach((truckName) => {
          rowsData.push({ param: `Самосвал: ${truckName}`, isSubHeader: true });

          const params = [
            { key: "arrivals", label: "  Подъездов", type: "num" },
            { key: "cycles", label: "  Всего ковшей", type: "num" },
            { key: "avg_cycles", label: "  └─ В среднем ковшей", type: "float" },
            { key: "volume", label: "  Объем породы (м³)", type: "float" },
            { key: "mass", label: "  Масса породы (т)", type: "mass" },
          ];

          params.forEach((p) => {
            const fleetVal = fleetTruckStats[truckName];
            let fleetDisplay = "";
            let fNum = 0;
            if (p.key === "arrivals") fNum = fleetVal.arrivals;
            if (p.key === "cycles") fNum = fleetVal.cycles;
            if (p.key === "avg_cycles") fNum = fleetVal.avg_cycles;
            if (p.key === "volume") fNum = fleetVal.vol;
            if (p.key === "mass") fNum = fleetVal.mass / 1000;

            if (p.type === "num") fleetDisplay = fNum;
            else fleetDisplay = Number(fNum).toFixed(1);

            const rData = { param: p.label, fleet: fleetDisplay, rawValues: [], isHeatmap: true, reverse: false };

            multiData.forEach((res) => {
              if (res.error || !res.data?.truck_stats_summary?.[truckName]) {
                rData.rawValues.push({ text: "-", num: null });
              } else {
                const tStat = res.data.truck_stats_summary[truckName];
                let val = 0;
                if (p.key === "arrivals") val = tStat.arrivals || 0;
                if (p.key === "cycles") val = tStat.cycles || 0;
                if (p.key === "avg_cycles") val = tStat.avg_cycles || 0;
                if (p.key === "volume") val = tStat.vol || 0;
                if (p.key === "mass") val = tStat.mass || 0;

                let text = p.type === "num" ? val : Number(val).toFixed(1);
                if (p.type === "mass") text = (val / 1000).toFixed(1);
                rData.rawValues.push({ text: text.toString(), num: val });
              }
            });
            rowsData.push(rData);
          });
        });
      }

      // --- ЗАПОЛНЕНИЕ ЛИСТА EXCEL ---
      rowsData.forEach(r => {
        if (r.isSpacer) {
          sheet.addRow([]).height = 10;
          return;
        }

        const rowDict = { param: r.param, fleet: r.fleet || "" };
        if (r.rawValues) {
          r.rawValues.forEach((d, i) => { rowDict[`dev_${i}`] = d.text; });
        }

        const xlRow = sheet.addRow(rowDict);
        xlRow.height = r.isHeader ? 25 : 20;

        xlRow.eachCell((cell, colNum) => {
          cell.alignment = { vertical: "middle", horizontal: colNum === 1 ? "left" : "center" };
          if (!r.isHeader && !r.isSubHeader) {
            cell.border = { bottom: { style: "thin", color: { argb: "FFCCCCCC" } } };
          }
        });

        if (r.isHeader) {
          xlRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
          xlRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF5A5A5A" } };
          return;
        }

        if (r.isSubHeader) {
          xlRow.font = { bold: true, color: { argb: "FF009900" } };
          xlRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
          return;
        }

        xlRow.getCell(2).font = { bold: true, color: { argb: "FF0000FF" } }; // Fleet column
        if (!r.param.startsWith("  ")) xlRow.font = { bold: true };

        let minV = Infinity, maxV = -Infinity;
        if (r.isHeatmap && r.rawValues) {
          r.rawValues.forEach(d => {
            if (d.num != null && !isNaN(d.num)) {
              if (d.num < minV) minV = d.num;
              if (d.num > maxV) maxV = d.num;
            }
          });
        }

        if (r.rawValues) {
          r.rawValues.forEach((d, i) => {
            const cell = xlRow.getCell(i + 3);
            if (d.num != null && !isNaN(d.num)) {
              if (r.isKpi) {
                const fill = getKpiFill(d.num);
                if (fill) cell.fill = fill;
              } else if (r.isHeatmap) {
                const fill = getHeatmapFill(d.num, minV, maxV, r.reverse);
                if (fill) cell.fill = fill;
              }
            }
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const filename = `Multi_Analytics_${dayjs().format("YYYY-MM-DD")}.xlsx`;
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, filename);
      toast.success(t("common.success"));
    } catch (error) {
      console.error("Excel Generation Error:", error);
      toast.error(t("common.error"));
    } finally {
      setLocalExporting(false);
    }
  };


  // --- Рендер Строки Таблицы (UI) ---
  const renderDataRow = ({ label, icon, isSubItem, fleetDisplay, fleetNum, isKpi, isHeatmap, reverseHeatmap, rawValues, metric }) => {
    let minV = Infinity, maxV = -Infinity;
    if (rawValues) {
      rawValues.forEach(d => {
        if (d.num != null && !isNaN(d.num)) {
          if (d.num < minV) minV = d.num;
          if (d.num > maxV) maxV = d.num;
        }
      });
    }

    return (
      <CTableRow key={label} className="border-bottom">
        <CTableDataCell className={`bg-light ${isSubItem ? "ps-4 text-muted" : "fw-semibold text-dark"}`} style={{ fontSize: isSubItem ? "0.9em" : "1em" }}>
          {icon && <span className="me-2">{icon}</span>}
          {label}
        </CTableDataCell>

        <CTableDataCell className="text-center fw-bold text-primary bg-light border-end">
          <div className="d-flex flex-column align-items-center justify-content-center">
            {fleetDisplay}
            {!isSubItem && <div className="css-sparkline d-none d-xl-block"></div>}
          </div>
        </CTableDataCell>

        {rawValues.map((res, i) => {

          let style = {};
          if (res.num != null && !isKpi && isHeatmap) {
            style = getUiHeatmapStyle(res.num, minV, maxV, reverseHeatmap);
          } else if (res.num != null && isKpi) {
            style = getUiKpiStyle(res.num);
          }

          const isTruckMetric = metric?.key?.includes('truck') || metric?.key?.includes('vehicle');
          const isHiddenForThisDevice = multiData[i]?.device?.hide_truck_stats;

          return (
            <CTableDataCell key={i} className="text-center position-relative align-middle" style={style}>
              <div className="d-flex align-items-center justify-content-center">
                <span className={res.num == null ? "text-danger small" : "fw-semibold"}>
                  {res.text}
                </span>
                {res.num != null && fleetNum != null && (
                  <TrendArrow val={res.num} avg={fleetNum} reverseHeatmap={reverseHeatmap} />
                )}
              </div>

              {/* Мини Stacked-Bar для времени */}
              {res.timeDistribution && (
                <div className="mini-stacked-bar">
                  <div style={{ width: `${res.timeDistribution.work}%`, background: '#2eb85c' }} title="В работе" />
                  <div style={{ width: `${res.timeDistribution.idle}%`, background: '#e55353' }} title="Простой" />
                  <div style={{ width: `${res.timeDistribution.nodata}%`, background: '#8a93a2' }} title="Нет данных" />
                </div>
              )}
            </CTableDataCell>
          );
        })}

        <CTableDataCell className="insight-cell text-wrap" style={{ width: "15%" }}>
          {generateInsight(label, rawValues, minV, maxV, reverseHeatmap)}
        </CTableDataCell>
      </CTableRow>
    );
  };

  return (
    <CModal visible={visible} onClose={onClose} size="xl" fullscreen="lg" alignment="center" scrollable>
      <CModalHeader closeButton className="bg-light">
        <CModalTitle>
          {t("dashboard.summary_title", "{i18n.t('summary_analytics_modal.summary_title', 'Сводная аналитика по')}")}{" "}
          {multiData.length || "выбранным"} {t("dashboard.devices", "{i18n.t('summary_analytics_modal.devices', 'устройствам')}")}
        </CModalTitle>
      </CModalHeader>

      <CModalBody className="bg-light p-3">
        {/* --- ПАНЕЛЬ ФИЛЬТРОВ --- */}
        <CCard className="mb-4 shadow-sm border-0">
          <CCardBody>
            <CRow className="align-items-end g-3">
              <CCol md={3}>
                <CFormLabel>{t("analysis.preset_select", "{i18n.t('analysis.preset_select', 'Шаблон периода')}")}</CFormLabel>
                <CFormSelect
                  value={multiPreset}
                  onChange={(e) => handleMultiPresetChange(e.target.value)}
                  className="fw-semibold"
                >
                  <option value="custom">{t("analysis.preset_custom", "{i18n.t('analysis.preset_custom', 'Произвольный')}")}</option>
                  <option value="today">{t("analysis.preset_today", "{i18n.t('analysis.preset_today', 'Сегодня (незаверш.)')}")}</option>
                  <option value="yesterday">{t("analysis.preset_yesterday", "{i18n.t('analysis.preset_yesterday', 'Вчера (00:00 - 23:59)')}")}</option>
                  <option value="last_2_days">{t("analysis.preset_2_days", "{i18n.t('analysis.preset_2_days', 'Последние 2 дня')}")}</option>
                  <option value="last_3_days">{t("analysis.preset_3_days", "{i18n.t('analysis.preset_3_days', 'Последние 3 дня')}")}</option>
                  <option value="last_7_days">{t("analysis.preset_7_days", "{i18n.t('analysis.preset_7_days', 'Последние 7 дней')}")}</option>
                  <option value="last_14_days">{t("analysis.preset_14_days", "{i18n.t('analysis.preset_14_days', 'Последние 14 дней')}")}</option>
                  <option value="last_month">{t("analysis.preset_month", "{i18n.t('analysis.preset_month', 'Месяц (последние 30 дней)')}")}</option>
                </CFormSelect>
              </CCol>

              <CCol md={3}>
                <CFormLabel>{t("common.date_start", "{i18n.t('common.date_start', 'Дата начала')}")}</CFormLabel>
                <DatePicker
                  selected={multiStartDate}
                  onChange={(date) => {
                    setMultiStartDate(date);
                    handleMultiPresetChange("custom");
                  }}
                  className="form-control"
                  showTimeSelect
                  dateFormat="dd.MM.yyyy HH:mm"
                  locale={currentLang === "en" ? "en-US" : currentLang}
                  popperProps={{ strategy: "fixed" }}
                  popperPlacement="top-start"
                  maxDate={multiEndDate}
                />
              </CCol>
              <CCol md={3}>
                <CFormLabel>{t("common.date_end", "{i18n.t('common.date_end', 'Дата окончания')}")}</CFormLabel>
                <DatePicker
                  selected={multiEndDate}
                  onChange={(date) => {
                    setMultiEndDate(date);
                    handleMultiPresetChange("custom");
                  }}
                  minDate={multiStartDate}
                  filterTime={(time) => { 
                    if (multiStartDate && time.toDateString() === multiStartDate.toDateString()) {
                      return time.getTime() > multiStartDate.getTime();
                    }
                    return true;
                  }}
                  className="form-control"
                  showTimeSelect
                  dateFormat="dd.MM.yyyy HH:mm"
                  locale={currentLang === "en" ? "en-US" : currentLang}
                  popperProps={{ strategy: "fixed" }}
                  popperPlacement="top-start"
                />
              </CCol>
              <CCol md={3}>
                <CButton
                  color="primary"
                  onClick={fetchMultiAnalytics}
                  disabled={multiLoading || (!isSuperuser && multiCooldown > 0)}
                  className="w-100 fw-bold"
                >
                  {multiLoading ? (
                    <><CSpinner size="sm" className="me-2" />Сбор данных...</>
                  ) : (!isSuperuser && multiCooldown > 0) ? (
                    `Ожидайте ${multiCooldown}с`
                  ) : (
                    t("dashboard.generate_report", "{i18n.t('dashboard.generate_report', 'Сформировать отчет')}")
                  )}
                </CButton>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        {multiData.length > 0 && !multiLoading && (
          <>
            <div className="mb-3 px-2">
              <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                <CButton
                  color="secondary"
                  variant="ghost"
                  className="fw-bold d-flex align-items-center gap-2 p-0 text-dark"
                  onClick={() => setShowMetricsFilter(!showMetricsFilter)}
                  style={{ textDecoration: "none" }}
                >
                  <CIcon icon={cilFilter} className="text-primary" />
                  {t("dashboard.displayed_params", "{i18n.t('dashboard.displayed_params', 'Отображаемые параметры:')}")}
                  <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                    {showMetricsFilter ? "▲ Скрыть" : "▼ Показать"}
                  </span>
                </CButton>
                <CButton
                  color="success"
                  size="sm"
                  variant="outline"
                  onClick={handleExportToExcel}
                  disabled={localExporting}
                >
                  {localExporting ? <CSpinner size="sm" /> : <><CIcon icon={cilCloudDownload} className="me-1" /> Excel</>}
                </CButton>
              </div>

              {showMetricsFilter && (
                <div className="d-flex flex-wrap gap-3 mt-3 p-3 bg-white border rounded shadow-sm">
                  {COMPARISON_METRICS.map((metric, index) => {
                    if (metric.isTwin && !multiData.some((res) => res.data?.use_digital_twin)) return null;
                    return (
                      <CFormCheck
                        key={`${metric.key}-${index}`}
                        id={`metric-${metric.key.replace(/\./g, '-')}-${index}`}
                        label={
                          <SafeTooltip content={metric.tooltip || metric.label}>
                            <span style={{ cursor: "help", borderBottom: "1px dashed #999" }}>
                              {metric.label}
                            </span>
                          </SafeTooltip>
                        }
                        checked={selectedMetrics.includes(metric.key)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedMetrics([...selectedMetrics, metric.key]);
                          else setSelectedMetrics(selectedMetrics.filter((m) => m !== metric.key));
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* --- ГРАФИКИ СРАВНЕНИЯ --- */}
            <CRow className="mb-4">
              <CCol md={6}>
                <CCard className="shadow-sm border-0 h-100">
                  <CCardHeader className="bg-white fw-bold d-flex justify-content-between">
                    Сравнение выработки (м³)
                  </CCardHeader>
                  <CCardBody style={{ height: "320px" }}>
                    {/* <--- ОБЕРТКА ДЛЯ ГРАФИКА ОБЪЕМОВ ---> */}
                    <ZoomableChartWrapper data={volumeChartData}>
                      {(slicedData) => (
                        <ResponsiveBar
                          data={slicedData}
                          keys={["volume"]}
                          indexBy="device"
                          margin={{ top: 20, right: 20, bottom: 80, left: 60 }}
                          padding={0.4}
                          colors={["#2db2a5"]}
                          borderRadius={2}
                          axisBottom={{
                            tickRotation: -45,
                            tickSize: 5,
                            tickPadding: 5
                          }}
                          markers={[{
                            axis: "y", value: avgVolume,
                            lineStyle: { stroke: "#333", strokeWidth: 2, strokeDasharray: "4 4" },
                            legend: "В среднем по парку",
                            legendOrientation: "horizontal", textStyle: { fontSize: 12, fill: '#333' }
                          }]}
                          tooltip={({ data }) => (
                            <div style={{ padding: "8px", background: "white", border: "1px solid #ccc", borderRadius: "4px" }}>
                              <strong>{data.full_name}</strong>: {data.volume} м³
                            </div>
                          )}
                        />
                      )}
                    </ZoomableChartWrapper>
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol md={6}>
                <CCard className="shadow-sm border-0 h-100">
                  <CCardHeader className="bg-white fw-bold">
                    Сравнение эффективности OEE (%)
                  </CCardHeader>
                  <CCardBody style={{ height: "320px" }}>
                    {/* <--- ОБЕРТКА ДЛЯ ГРАФИКА OEE ---> */}
                    <ZoomableChartWrapper data={oeeChartData}>
                      {(slicedData) => (
                        <ResponsiveBar
                          data={slicedData}
                          keys={["oee"]}
                          indexBy="device"
                          margin={{ top: 20, right: 20, bottom: 80, left: 60 }}
                          padding={0.4}
                          colors={["#e88b39"]}
                          borderRadius={2}
                          axisBottom={{
                            tickRotation: -45,
                            tickSize: 5,
                            tickPadding: 5
                          }}
                          markers={[{
                            axis: "y", value: avgOEE,
                            lineStyle: { stroke: "#333", strokeWidth: 2, strokeDasharray: "4 4" },
                            legend: "В среднем по парку",
                            legendOrientation: "horizontal", textStyle: { fontSize: 12, fill: '#333' }
                          }]}
                          tooltip={({ data }) => (
                            <div style={{ padding: "8px", background: "white", border: "1px solid #ccc", borderRadius: "4px" }}>
                              <strong>{data.full_name}</strong>: {data.oee}%
                            </div>
                          )}
                        />
                      )}
                    </ZoomableChartWrapper>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            {/* --- ТАБЛИЦА СРАВНЕНИЯ ОСНОВНЫХ KPI --- */}
            <CCard className="mb-4 shadow-sm border-0 overflow-hidden">
              <div className="table-responsive">
                <CTable align="middle" className="mb-0 border-0" small bordered>
                  <CTableHead color="light">
                    <CTableRow>
                      <CTableHeaderCell style={{ width: "20%" }}>
                        {t("dashboard.parameter", "Параметр")}
                      </CTableHeaderCell>
                      <CTableHeaderCell className="text-center text-primary" style={{ width: "15%" }}>
                        В среднем по парку {/* Переведено */}
                      </CTableHeaderCell>
                      {multiData.map((res, i) => (
                        <CTableHeaderCell key={i} className="text-center bg-primary text-white bg-opacity-75">
                          {res.device.name}
                        </CTableHeaderCell>
                      ))}
                      <CTableHeaderCell style={{ width: "15%", backgroundColor: "#fffbe6" }}>
                        {i18n.t('dashboard.output', 'Вывод')} {/* Переведено */}
                      </CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>

                  <CTableBody className="bg-white">
                    {COMPARISON_METRICS.filter((m) => selectedMetrics.includes(m.key)).map((metric) => {
                      if (metric.isTwin && !multiData.some((res) => res.data?.use_digital_twin)) return null;

                      let fleetDisplay = "";
                      let fleetNum = null;
                      const rawValues = [];
                      const isSubItem = metric.key.includes("empty_sec") || metric.key.includes("full_sec") || metric.key.includes("unload_sec");
                      const isReverse = metric.key.includes("downtime") || metric.key.includes("no_data") || metric.type.includes("avg_time") || metric.type.includes("avg_sec");

                      // Обработка логики метрик
                      if (metric.type === "useful_time") {
                        const f = getFleetMetricValue(metric.key, metric.type);
                        fleetNum = f.tUptime > 0 ? (f.tWork / f.tUptime) * 100 : 0;
                        fleetDisplay = `${(f.tWork / 3600).toFixed(1)} маш.-ч`;

                        multiData.forEach((res) => {
                          if (res.error || !res.data) { rawValues.push({ text: "Нет данных", num: null, deviceName: res.device.name }); return; }

                          const uSec = res.data.kpis?.tWork || 0;
                          const dSec = res.data.durations_by_class_sec?.actual_downtime || 0;
                          const nSec = res.data.durations_by_class_sec?.no_data || 0;
                          const totalS = Math.max(1, res.data.total_duration_sec || 1);

                          const tMh = Math.max(0, totalS - nSec) / 3600;
                          const pct = tMh > 0 ? ((uSec / 3600) / tMh) * 100 : 0;

                          rawValues.push({
                            text: `${(uSec / 3600).toFixed(1)} ч`,
                            num: pct,
                            deviceName: res.device.name,
                            timeDistribution: {
                              work: (uSec / totalS) * 100,
                              idle: (dSec / totalS) * 100,
                              nodata: (nSec / totalS) * 100
                            }
                          });
                        });
                      } else {
                        // ИСПРАВЛЕНИЕ: Используем агрегированные данные по парку
                        fleetNum = getFleetMetricValue(metric.key, metric.type);
                        fleetDisplay = metric.key === "durations_by_class_sec.actual_downtime"
                          ? formatMetric(fleetNum + getFleetMetricValue("durations_by_class_sec.no_data", "sum_time"), metric.type)
                          : formatMetric(fleetNum, metric.type);

                        multiData.forEach((res) => {
                          if (res.error || !res.data) { rawValues.push({ text: "Нет данных", num: null, deviceName: res.device.name }); return; }
                          let v = getMetricValue(res.data, metric.key);
                          if (metric.key === "durations_by_class_sec.actual_downtime") v += getMetricValue(res.data, "durations_by_class_sec.no_data");
                          rawValues.push({ text: formatMetric(v, metric.type), num: v, deviceName: res.device.name });
                        });
                      }

                      return renderDataRow({
                        label: metric.label,
                        isSubItem, fleetDisplay, fleetNum, isKpi: metric.type === "pct",
                        isHeatmap: metric.type !== "pct", reverseHeatmap: isReverse, rawValues,
                        metric
                      });
                    })}
                  </CTableBody>
                </CTable>
              </div>
            </CCard>

            {/* --- ДЕТАЛИЗАЦИЯ ПО СМЕНАМ (Древовидная) --- */}
            {(() => {
              const allShiftNames = [...new Set(multiData.filter(r => !r.error && r.data?.shift_stats).flatMap(r => Object.keys(r.data.shift_stats)))];
              if (allShiftNames.length === 0) return null;

              return (
                <CCard className="mb-4 shadow-sm border-0 overflow-hidden">
                  <CCardHeader className="bg-primary text-white fw-bold d-flex align-items-center">
                    <CIcon icon={cilFilter} className="me-2" /> Детализация по сменам
                  </CCardHeader>
                  <div className="table-responsive">
                    <CTable hover align="middle" className="mb-0 border-0" small bordered>
                      <CTableHead color="light">
                        <CTableRow>
                          <CTableHeaderCell style={{ width: "20%" }}>{t("dashboard.parameter", "Параметр")}</CTableHeaderCell>
                          <CTableHeaderCell className="text-center" style={{ width: "15%" }}>-</CTableHeaderCell>
                          {multiData.map((res, i) => <CTableHeaderCell key={i} className="text-center bg-primary text-white bg-opacity-75">{res.device.name}</CTableHeaderCell>)}
                          <CTableHeaderCell style={{ width: "15%", backgroundColor: "#fffbe6" }}>Вывод</CTableHeaderCell>
                        </CTableRow>
                      </CTableHead>
                      <CTableBody className="bg-white">
                        {allShiftNames.map((shiftName) => (
                          <React.Fragment key={shiftName}>
                            <CTableRow color="info">
                              <CTableDataCell colSpan={multiData.length + 3} className="fw-bold">
                                🕒 Смена: {shiftName}
                              </CTableDataCell>
                            </CTableRow>
                            {[
                              { key: "cycles_tot", label: "🔄 Всего циклов", type: "num" },
                              { key: "cycles_veh", label: "├─ В самосвал", type: "num", sub: true },
                              { key: "cycles_gnd", label: "└─ В отвал", type: "num", sub: true },
                              { key: "vol_tot", label: "📦 Объем (всего) м³", type: "float" },
                              { key: "vol_veh", label: "├─ В самосвал м³", type: "float", sub: true },
                              { key: "vol_gnd", label: "└─ В отвал м³", type: "float", sub: true },
                              { key: "mass_tot", label: "⚖️ Масса (всего) т", type: "mass" },
                              { key: "mass_veh", label: "├─ В самосвал т", type: "mass", sub: true },
                              { key: "mass_gnd", label: "└─ В отвал т", type: "mass", sub: true },
                              { key: "dur_dig", label: "⛏ Копание / Поворот", type: "time" },
                              { key: "dur_unload_veh", label: "├─ Разгрузка в авто", type: "time", sub: true },
                              { key: "dur_unload_gnd", label: "└─ Разгрузка в отвал", type: "time", sub: true },
                              { key: "downtime", label: "Отсутствие видимости", type: "time", reverse: true },
                            ].map((p) => {
                              const rawValues = [];
                              multiData.forEach((res) => {
                                if (res.error || !res.data?.shift_stats?.[shiftName]) { rawValues.push({ text: "-", num: null, deviceName: res.device.name }); return; }
                                const s = res.data.shift_stats[shiftName];
                                let val = 0;
                                if (p.key === "cycles_tot") val = s.cycles_total || 0;
                                if (p.key === "cycles_veh") val = s.cycles_vehicle || 0;
                                if (p.key === "cycles_gnd") val = s.cycles_ground || 0;
                                if (p.key === "vol_tot") val = s.volume_total || 0;
                                if (p.key === "vol_veh") val = s.volume_vehicle || 0;
                                if (p.key === "vol_gnd") val = s.volume_ground || 0;
                                if (p.key === "mass_tot") val = s.mass_total || 0;
                                if (p.key === "mass_veh") val = s.mass_vehicle || 0;
                                if (p.key === "mass_gnd") val = s.mass_ground || 0;
                                if (p.key === "dur_dig") val = (s.durations?.[3] || 0) + (s.durations?.[0] || 0);
                                if (p.key === "dur_unload_veh") val = s.durations?.[1] || 0;
                                if (p.key === "dur_unload_gnd") val = s.durations?.[2] || 0;
                                if (p.key === "downtime") val = (s.actual_downtime || 0) + (s.no_data || 0);

                                let text = val;
                                if (p.type === "float") text = Number(val).toFixed(1);
                                if (p.type === "mass") { val = val / 1000; text = val.toFixed(1); }
                                if (p.type === "time") text = formatDuration(val);
                                rawValues.push({ text: text.toString(), num: val, deviceName: res.device.name });
                              });
                              return renderDataRow({ label: p.label, isSubItem: p.sub, fleetDisplay: "-", fleetNum: null, isHeatmap: true, reverseHeatmap: p.reverse, rawValues, metric: p });
                            })}
                          </React.Fragment>
                        ))}
                      </CTableBody>
                    </CTable>
                  </div>
                </CCard>
              );
            })()}

            {/* --- СТАТИСТИКА ПО САМОСВАЛАМ (Древовидная) --- */}
            {(() => {
              const allTruckNames = Object.keys(fleetTruckStats);
              if (allTruckNames.length === 0) return null;
              return (
                <CCard className="mb-4 shadow-sm border-0 overflow-hidden">
                  <CCardHeader className="bg-success text-white fw-bold">
                    🚛 {t("analysis.truck_stats_title", "{i18n.t('analysis.truck_stats_title', 'Статистика по самосвалам')}")}
                  </CCardHeader>
                  <div className="table-responsive">
                    <CTable hover align="middle" className="mb-0 border-0" small bordered>
                      <CTableHead color="light">
                        <CTableRow>
                          <CTableHeaderCell style={{ width: "20%" }}>{t("dashboard.parameter", "Параметр")}</CTableHeaderCell>
                          <CTableHeaderCell className="text-center text-success" style={{ width: "15%" }}>СУММА ПО ПАРКУ</CTableHeaderCell>
                          {multiData.map((res, i) => <CTableHeaderCell key={i} className="text-center bg-primary text-white bg-opacity-75">{res.device.name}</CTableHeaderCell>)}
                          <CTableHeaderCell style={{ width: "15%", backgroundColor: "#fffbe6" }}>Вывод</CTableHeaderCell>
                        </CTableRow>
                      </CTableHead>
                      <CTableBody className="bg-white">
                        {allTruckNames.map((truckName) => (
                          <React.Fragment key={truckName}>
                            <CTableRow color="success">
                              <CTableDataCell colSpan={multiData.length + 3} className="fw-bold text-dark">
                                🚚 Самосвал: {truckName}
                              </CTableDataCell>
                            </CTableRow>
                            {[
                              { key: "arrivals", label: "🚩 Подъездов", type: "num" },
                              { key: "cycles", label: "🏗️ Всего ковшей", type: "num" },
                              { key: "avg_cycles", label: "└─ В среднем ковшей", type: "float", sub: true },
                              { key: "volume", label: "📦 Объем породы (м³)", type: "float" },
                              { key: "mass", label: "⚖️ Масса породы (т)", type: "mass" },
                            ].map((p) => {
                              const rawValues = [];
                              multiData.forEach((res) => {
                                if (res.error || !res.data?.truck_stats_summary?.[truckName]) { rawValues.push({ text: "-", num: null, deviceName: res.device.name }); return; }
                                const tStat = res.data.truck_stats_summary[truckName];
                                let val = 0;
                                if (p.key === "arrivals") val = tStat.arrivals || 0;
                                if (p.key === "cycles") val = tStat.cycles || 0;
                                if (p.key === "avg_cycles") val = tStat.avg_cycles || 0;
                                if (p.key === "volume") val = tStat.vol || 0;
                                if (p.key === "mass") val = tStat.mass || 0;

                                let text = val;
                                if (p.type === "float") text = Number(val).toFixed(1);
                                if (p.type === "mass") { val = val / 1000; text = val.toFixed(1); }
                                rawValues.push({ text: text.toString(), num: val, deviceName: res.device.name });
                              });

                              let fVal = fleetTruckStats[truckName][p.key];
                              if (p.key === "avg_cycles") fVal = fleetTruckStats[truckName].avg_cycles;
                              if (p.key === "volume") fVal = fleetTruckStats[truckName].vol;
                              if (p.key === "mass") fVal = fleetTruckStats[truckName].mass / 1000;
                              const fText = p.type === "num" ? fVal : Number(fVal).toFixed(1);

                              return renderDataRow({ label: p.label, isSubItem: p.sub, fleetDisplay: fText, fleetNum: fVal, isHeatmap: true, reverseHeatmap: false, rawValues, metric: p });
                            })}
                          </React.Fragment>
                        ))}
                      </CTableBody>
                    </CTable>
                  </div>
                </CCard>
              );
            })()}

            {/* --- СТОЛБЧАТЫЕ ДИАГРАММЫ ВРЕМЕНИ (Замена круговым) --- */}
            <h5 className="mb-3 mt-4">
              {t("dashboard.time_distribution", "{i18n.t('dashboard.time_distribution', 'Распределение времени работы')}")}
            </h5>
            <CRow>
              {multiData.map((res, i) => {
                if (res.error || !res.data || !res.data.durations_by_class_sec) return null;
                const durs = res.data.durations_by_class_sec;

                // Формируем данные индивидуально для каждого устройства в массиве
                const tWork = res.data.kpis?.tWork || 0;
                const engineOn = durs.actual_downtime || 0;
                const engineOff = durs.no_data || 0;
                const totalDowntime = engineOn + engineOff;

                const timeBarData = [
                  {
                    id: "Полезное время",
                    label: "Полезное",
                    value: tWork,
                    color: "#2eb85c",
                    desc: "Сумма временных интервалов, в которых техника совершала активную работу."
                  },
                  {
                    id: "Всего простоев",
                    label: "Простои",
                    value: totalDowntime,
                    color: "#e55353",
                    desc: "Сумма временных интервалов, в которых техника не совершала активной работы."
                  },
                  {
                    id: "Простой с работающим двигателем",
                    label: "Двиг. вкл.",
                    value: engineOn,
                    color: "#f9b115",
                    desc: "Сумма простоев, в которых двигатель работал (масса в рамках бортовой электроцепи экскаватора включена)."
                  },
                  {
                    id: "Простой с выключенным двигателем",
                    label: "Двиг. выкл.",
                    value: engineOff,
                    color: "#63c2de",
                    desc: "Сумма простоев, в которых двигатель был заглушен (масса в рамках бортовой электроцепи экскаватора выключена)."
                  },
                ];

                const BreakdownLayer = ({ bars }) => {
                  const barOn = bars.find(b => b.data.indexValue === "Двиг. вкл.");
                  const barOff = bars.find(b => b.data.indexValue === "Двиг. выкл.");
                  if (!barOn || !barOff) return null;

                  const startX = barOn.x;
                  const endX = barOff.x + barOff.width;
                  const midX = startX + (endX - startX) / 2;

                  const highestBarY = Math.min(barOn.y, barOff.y);
                  const bracketY = highestBarY - 25;

                  return (
                    <g>
                      <path
                        d={`M ${startX + barOn.width / 2},${highestBarY - 10} L ${startX + barOn.width / 2},${bracketY} L ${endX - barOff.width / 2},${bracketY} L ${endX - barOff.width / 2},${highestBarY - 10}`}
                        fill="none"
                        stroke="#adb5bd"
                        strokeWidth={1.5}
                        strokeDasharray="4 4"
                      />
                      <rect x={midX - 70} y={bracketY - 10} width={140} height={20} fill="white" rx={4} />
                      <text x={midX} y={bracketY + 4} textAnchor="middle" fill="#6c757d" fontSize="11" fontWeight="bold">
                        Детализация простоев
                      </text>
                    </g>
                  );
                };

                return (
                  <CCol lg={6} md={12} key={i} className="mb-4">
                    <CCard className="h-100 shadow-sm border-0 pt-2">
                      <div className="text-center fw-bold text-truncate px-2" title={res.device.name}>
                        {res.device.name}
                      </div>
                      <CCardBody style={{ height: "350px" }}>
                        {timeBarData.every(d => d.value === 0) ? (
                          <div className="d-flex h-100 align-items-center justify-content-center text-muted">
                            {t("dashboard.no_data", "Нет данных")}
                          </div>
                        ) : (
                          <ResponsiveBar
                            data={timeBarData}
                            keys={["value"]}
                            indexBy="label"
                            margin={{ top: 50, right: 20, bottom: 40, left: 60 }}
                            padding={0.3}
                            valueScale={{ type: "linear" }}
                            colors={({ data }) => data.color}
                            colorBy="indexValue"
                            axisTop={null}
                            axisRight={null}
                            axisBottom={{
                              tickSize: 5,
                              tickPadding: 5,
                              tickRotation: 0,
                            }}
                            axisLeft={{
                              tickSize: 5,
                              tickPadding: 5,
                              legend: "Время (часы)",
                              legendPosition: "middle",
                              legendOffset: -50,
                              format: (v) => `${(v / 3600).toFixed(1)}ч`
                            }}
                            enableLabel={false}
                            animate={true}
                            motionConfig="gentle"
                            layers={["grid", "axes", "bars", BreakdownLayer, "markers"]}
                            tooltip={({ data }) => (
                              <div
                                style={{
                                  padding: "12px",
                                  background: "white",
                                  border: `1px solid ${data.color}`,
                                  borderRadius: "6px",
                                  boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
                                  maxWidth: "260px",
                                  whiteSpace: "normal"
                                }}
                              >
                                <div style={{ color: data.color, fontWeight: "bold", fontSize: "1rem", borderBottom: "1px solid #eee", paddingBottom: "6px", marginBottom: "8px" }}>
                                  {data.id}: <br /><span className="text-dark fs-5">{formatDuration(data.value)}</span>
                                </div>
                                <div style={{ fontSize: "0.8rem", color: "#6c757d", lineHeight: "1.3" }}>
                                  {data.desc}
                                </div>
                              </div>
                            )}
                          />
                        )}
                      </CCardBody>
                    </CCard>
                  </CCol>
                );
              })}
            </CRow>

          </>
        )}
      </CModalBody>
    </CModal>
  );
};

export default SummaryAnalyticsModal;